import { ActionTree, ActionContext, Action } from "vuex";
import State from "./state";
import moment from "moment";
import axios, { AxiosResponse } from "axios";
import { MutationNames } from "@/store/mutations";
import {
  JobDetail,
  ExamDetail,
  PartsDetail,
  Part,
  ProductItem,
  Question
} from "../common/interfaces";
import { ContractTypes, JobTypes } from "../common/constants";
import * as serviceChargeCalulator from "../components/completeJob/serviceChargeCalulator";

// import store from "@/store/store";

let divisionRegions: { [k: string]: string } | null = null;

// function for creating a cache of division regions. These are used
// to find out a divisions region when loading the job detail
async function loadDivisionRegions(store: ActionContext<State, any>) {
  divisionRegions = {};
  const regionResponse = await axios.get(
    `${store.state.Environment.ODataBaseUrl}/DivisionRegions()`,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );

  if (regionResponse.status === 200 && regionResponse.data) {
    const regions = regionResponse.data.value;
    regions.forEach((r: any) => {
      divisionRegions![r.RegionNumber] = r.RegionDescription;
    });
  }
}

async function loadQuestions(store: ActionContext<State, any>) {
  if (store.state.Job.JobDetail) {
    const response = await axios.get(
      `${
        store.state.Environment.ODataBaseUrl
      }/ChecklistQuestions?$filter=Inventorytype eq '${
        store.state.Job.JobDetail!.InventoryTypeId
      }'&$expand=SubgroupType($select=Description)`,
      {
        headers: {
          Authorization: "Bearer " + store.state.User.AccessToken
        }
      }
    );
    const newQADictionary: { [qk: string]: string } = {};

    if (response.status === 200 && response.data) {
      const questions = response.data.value;
      questions.forEach((q: any) => {
        const question: Question = {
          QuestionId: q.Questionkey,
          InventoryType: q.Inventorytype,
          QuestionText: q.Questiontext,
          QuestionSubgroupTitle: q.SubgroupTypeCode
            ? q.SubgroupType.Description
            : null
        };
        newQADictionary[question.QuestionId] = "";
      });
      store.commit(MutationNames.SetChecklistQA, newQADictionary);
    }
  }
}

function getNextExamDate(contractCode: string, currentDueDate: moment.Moment) {
  let nextExaminationDate = currentDueDate;
  switch (contractCode || "") {
    case ContractTypes.ONE_SERVICE:
      nextExaminationDate = nextExaminationDate.add(1, "year");
      break;
    case ContractTypes.ONE_SERVICE_ONE_INSPECTION:
    case ContractTypes.TWO_SERVICES:
      nextExaminationDate = nextExaminationDate.add(6, "months");
      break;
    case ContractTypes.TWO_SERVICES_TWO_INSPECTIONS:
      nextExaminationDate = nextExaminationDate.add(3, "months");
      break;
    default:
      nextExaminationDate = nextExaminationDate.add(6, "months");
      break;
  }
  return nextExaminationDate;
}

function getNextJobType(
  contractType: string,
  jobType: string
): string | undefined {
  switch (contractType) {
    case ContractTypes.ONE_SERVICE:
    case ContractTypes.TWO_SERVICES:
      return JobTypes.SERVICE;
    case ContractTypes.ONE_SERVICE_ONE_INSPECTION:
    case ContractTypes.TWO_SERVICES_TWO_INSPECTIONS:
      return jobType === JobTypes.SERVICE
        ? JobTypes.INSPECTION
        : JobTypes.SERVICE;
    default:
      return undefined;
  }
}

function checkForServiceProduct(
  store: ActionContext<State, any>,
  serviceProduct: string
): boolean {
  return store.state.Job.PartsDetail!.Parts.some((p: Part) => {
    return p.ProductItemId === serviceProduct;
  });
}

// Patch helper function
async function patchObject(
  store: ActionContext<State, any>,
  entityType: string,
  primaryKey: number,
  updateObject: object
): Promise<AxiosResponse<any>> {
  return await axios.patch(
    `${store.state.Environment.ODataBaseUrl}/${entityType}(${primaryKey})`,
    updateObject,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );
}

// Post helper function
export async function postObject(
  store: ActionContext<State, any>,
  entityType: string,
  object: object
): Promise<AxiosResponse<any>> {
  return await axios.post(
    `${store.state.Environment.ODataBaseUrl}/${entityType}()`,
    object,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );
}

// Post Email Function
export async function postEmail(
  store: ActionContext<State, any>,
  entityType: string,
  object: object
): Promise<AxiosResponse<any>> {
  return await axios.post(
    `${store.state.Environment.ODataBaseUrl}/${entityType}/SendMessage()`,
    object,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );
}

// Post helper function Signature
export async function postSignature(
  store: ActionContext<State, any>,
  entityType: string,
  documentId: string,
  base64image: string
): Promise<AxiosResponse<any>> {
  return await axios.post(
    `${store.state.Environment.ODataBaseUrl}/${entityType}(${documentId})`,
    base64image,
    {
      headers: {
        authorization: "Bearer " + store.state.User.AccessToken,
        "content-type": "application/octet-stream",
        "x-filename": "signature.jpg",
        "x-locale": "en-GB",
        "x-base64": "true"
      }
    }
  );
}

// Delete helper function
async function deleteObject(
  store: ActionContext<State, any>,
  entityType: string,
  primaryKey: number
): Promise<AxiosResponse<any>> {
  return await axios.delete(
    `${store.state.Environment.ODataBaseUrl}/${entityType}(${primaryKey})`,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );
}
export interface ToggleQuestion {
  question: string;
  value?: boolean | null;
  function?: (value: boolean | null, question: ToggleQuestion) => void;
  entityFieldName?: string;
  storeFieldName?: string;
  textOnly?: boolean;
  customRequiredCheck?: () => boolean;
}
// classes are used as enums here so ignore this rule
// tslint:disable-next-line:max-classes-per-file
export class ActionNames {
  public static SetJobId: string = "setJobId";
  public static SetJobDetail: string = "setJobDetail";
  public static SetExamDetail: string = "setExamDetail";
  public static SetPartsDetail: string = "setPartsDetail";
  public static SetParts: string = "setParts";
  public static SetChecklistQA: string = "setChecklistQA";
  public static SaveChecklistQARow: string = "saveChecklistQARow";
  public static SaveBookingDate: string = "saveBookingDate";
  public static SaveBookingTime: string = "saveBookingTime";
  public static SetBookedInDateTime: string = "setBookedInDateTime";
  public static SetProofLoad: string = "setProofLoad";
  public static SetRadius: string = "setRadius";
  public static SetArrivedOnSite: string = "setArrivedOnSite";
  public static SetDefectDetails: string = "setDefectDetails";
  public static SetDefectRemedy: string = "setDefectRemedy";
  public static SetDefectWork: string = "setDefectWork";
  public static SetInaccessibleParts: string = "setInaccessibleParts";
  public static SetVisitNotes: string = "setVisitNotes";
  public static SetLiftSafe: string = "setLiftSafe";
  public static SetSafeWorkingLoad: string = "setSafeWorkingLoad";
  public static CreateQuote: string = "createQuote";
  public static AddPartToQuote: string = "addPartToQuote";
  public static RemovePartFromQuote: string = "removePartFromQuote";
  public static CloseJob: string = "closeJob";
  public static SetRoteIncluded: string = "setRoteIncluded";
  public static SetNextRoteDue: string = "setNextRoteDue";
  public static SetRoteToggleQuestion: string = "setRoteToggleQuestion";
  public static SetAdditionalParticulars: string = "setAdditionalParticulars";
}
async function setAdditionalParticulars(
  store: ActionContext<State, any>,
  additionalParticulars: string
) {
  if (
    store.state.Job.ExamDetail!.AddParticulars ||
    "" !== additionalParticulars
  ) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.AddParticulars;
    store.commit(MutationNames.SetAdditionalParticulars, additionalParticulars);
    try {
      const updateObject = {
        ExamAddParticulars: additionalParticulars
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetAdditionalParticulars, rollbackValue);
        throw new Error("Failed to update Additional Particulars");
      }
    } catch (err) {
      store.commit(MutationNames.SetAdditionalParticulars, rollbackValue);
      throw new Error("Failed to update Additional Particulars");
    }
  }
}
async function setRoteToggleQuestion(
  store: ActionContext<State, any>,
  payLoad: { question: ToggleQuestion; newValue: boolean | null }
) {
  const examDetail = store.state.Job.ExamDetail as any;
  if (examDetail[payLoad.question.storeFieldName!] !== payLoad.newValue) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = examDetail[payLoad.question.storeFieldName!];
    store.commit(MutationNames.SetRoteToggleQuestion, payLoad);
    try {
      const updateObject = {
        [payLoad.question.entityFieldName!]: payLoad.newValue
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        payLoad.newValue = rollbackValue;
        store.commit(MutationNames.SetRoteToggleQuestion, payLoad);
        throw new Error("Failed to update " + payLoad.question.entityFieldName);
      }
    } catch (err) {
      payLoad.newValue = rollbackValue;
      store.commit(MutationNames.SetRoteToggleQuestion, payLoad);
      throw new Error("Failed to update " + payLoad.question.entityFieldName);
    }
  }
}

function setJobId(store: ActionContext<State, any>, jobId: number) {
  if (jobId !== store.state.Job.JobId || store.state.Job.JobId === 0) {
    store.commit(MutationNames.SetJobId, jobId);
    store.commit(MutationNames.SetJobDetail, undefined);
    store.commit(MutationNames.SetExamDetail, undefined);
    store.commit(MutationNames.SetPartsDetail, undefined);
    store.commit(MutationNames.SetChecklistQA, undefined);
  }
}

async function setJobDetail(store: ActionContext<State, any>, jobId: number) {
  store.dispatch(ActionNames.SetJobId, jobId);

  // Use Job Id to query all needed Job Detail information
  // This only needs doing if the job detail is currently undefined
  if (!store.state.Job.JobDetail) {
    const jobResponse = await axios.get(
      `${store.state.Environment.ODataBaseUrl}/Problems(${
        store.state.Job.JobId
      })?$select=ProblemId,Description,Created,CustomerReference&` +
        `$expand=ProblemXtra($select=JobNotes,JobDate, BookedInDateTime, ExamDueDate; ` +
        `$expand=Engineer($select=UserCode,UserName)),` +
        `Type1($select=Type1Id,Description),` +
        `Status($select=Deadflag),` +
        `Contact($select=ContactId,Forename,Surname,PhoneNumber,Email;` +
        `$expand=AdditionalEmails($select=Email)),` +
        `Division($select=Name,PhoneNumber,OperatingCompanyCode;$expand=DivisionXtra($select=RegionNumber;),` +
        `Company($select=Name)), ` +
        `Inventory($select=InventoryId,SerialNumber,ProductItemId,Description,Commissioned,TypeId, Location; ` +
        `$expand=Division($select=DivisionId,Name,PhoneNumber,OperatingCompanyCode;` +
        `$expand=Address($select=AddressLine1,AddressLine2,AddressLine3,AddressLine4,Country,Postcode)` +
        `,DivisionXtra($select=RegionNumber;)` +
        `,Company($select=CompanyId,Name)),InventoryXtra(` +
        `$select=NextVisitDate, VehicleMake,OtherId,Swl,Height,PowerPack,Ram,Motor,Angle,NotesForNextVisit,StandardFlagField5, StandardDateField5 ` +
        `;$expand=InventoryContract($select=ContractDescription))` +
        `
      )`,
      {
        headers: {
          Authorization: "Bearer " + store.state.User.AccessToken
        }
      }
    );

    if (jobResponse.status === 200 && jobResponse.data) {
      const j = jobResponse.data.value[0];
      // A seperate query is needed to find the region name to make up the region code
      // Do this before creating the job
      let regionCode = j.Inventory.Division.DivisionXtra.RegionNumber
        ? j.Inventory.Division.DivisionXtra.RegionNumber
        : "00";

      if (!divisionRegions) {
        await loadDivisionRegions(store);
      }

      if (divisionRegions![regionCode]) {
        regionCode = `${regionCode} ${divisionRegions![regionCode]}`;
      }

      const jobDetail: JobDetail = {
        JobDescription: j.Description,
        CreatedDate: moment(j.Created),
        CustomerReference: j.CustomerReference,
        EngineerCode: j.ProblemXtra.Engineer.UserCode,
        EngineerName: j.ProblemXtra.Engineer.UserName,
        JobNotes: j.ProblemXtra.JobNotes,
        JobDate: moment(j.ProblemXtra.JobDate),
        JobType: j.Type1.Type1Id,
        TypeDescription: j.Type1.Description,
        StatusDeadFlag: j.Status.Deadflag,
        ContactId: j.Contact ? j.Contact.ContactId : null,
        ContactForename: j.Contact ? j.Contact.Forename : null,
        ContactSurname: j.Contact ? j.Contact.Surname : null,
        ContactNumber: j.Contact ? j.Contact.PhoneNumber : null,
        ContactEmail: (() => {
          if (j.Contact) {
            if (j.Contact.Email) {
              return j.Contact.Email;
            } else if (j.Contact.AdditionalEmails.length > 0) {
              return j.Contact.AdditionalEmails[0].Email;
            }
          } else {
            return null;
          }
        })(),
        DivisionName: j.Inventory.Division.Name,
        DivisionNumber: j.Inventory.Division.PhoneNumber,
        DivisionOperatingCompanyCode: j.Inventory.Division.OperatingCompanyCode,
        DivisionRegionCode: regionCode,
        CompanyName: j.Inventory.Division.Company.Name,
        AddressLine1: j.Inventory.Division.Address.AddressLine1,
        AddressLine2: j.Inventory.Division.Address.AddressLine2,
        AddressLine3: j.Inventory.Division.Address.AddressLine3,
        AddressLine4: j.Inventory.Division.Address.AddressLine4,
        AddressCountry: j.Inventory.Division.Address.Country,
        AddressPostcode: j.Inventory.Division.Address.Postcode,
        InventoryId: j.Inventory.InventoryId,
        InventoryProduct: j.Inventory.ProductItemId,
        InventorySerialId: j.Inventory.SerialNumber,
        InventoryDescription: j.Inventory.Description,
        Commissioned: moment(j.Inventory.Commissioned),
        InventoryTypeId: j.Inventory.TypeId,
        InventoryLocation: j.Inventory.Location,
        NextVisitDate: moment(j.Inventory.InventoryXtra.NextVisitDate),
        VehicleMake: j.Inventory.InventoryXtra.VehicleMake,
        OtherId: j.Inventory.InventoryXtra.OtherId,
        Swl: j.Inventory.InventoryXtra.Swl,
        Height: j.Inventory.InventoryXtra.Height,
        PowerPack: j.Inventory.InventoryXtra.PowerPack,
        Ram: j.Inventory.InventoryXtra.Ram,
        Motor: j.Inventory.InventoryXtra.Motor,
        Angle: j.Inventory.InventoryXtra.Angle,
        NotesForNextVisit: j.Inventory.InventoryXtra.NotesForNextVisit,
        BookedInDateTime: j.ProblemXtra.BookedInDateTime
          ? moment(j.ProblemXtra.BookedInDateTime)
          : j.ProblemXtra.JobDate
            ? moment(j.ProblemXtra.JobDate)
            : undefined,
        DueDate: moment(j.ProblemXtra.ExamDueDate),
        ROTEIncluded: j.Inventory.InventoryXtra.StandardFlagField5,
        NextRoteDue: j.Inventory.InventoryXtra.StandardDateField5,
        ContractDescription: j.Inventory.InventoryXtra.InventoryContract
          ? j.Inventory.InventoryXtra.InventoryContract.ContractDescription
          : undefined
      };

      store.commit(MutationNames.SetJobDetail, jobDetail);
    }
  }
}

async function setExamDetail(store: ActionContext<State, any>, jobId: number) {
  store.dispatch(ActionNames.SetJobId, jobId);

  if (!store.state.Job.ExamDetail) {
    const examDetailResponse = await axios.get(
      `${store.state.Environment.ODataBaseUrl}//Problems(${jobId})?` +
        `$select=ProblemId&` +
        `$expand=ProblemXtra(` +
        `$select=JobDate,ExamProofLoad,ExamRadius,ExamLiftSafe,ExamDefectDetails,ExamDefectRemedy,` +
        `ExamDefectWork,EeInaccessible,ExamLeftSite,ExamArrivedOnSite,ExamVisitNotes,BookedInDateTime,` +
        `ExamFactoryThorough,ExamFirstAfterInstall,ExamEqInstallCorrect,ExamWithin6Months,ExamWithin12Months,ExamInSchedule,ExamAfterExcept, ExamAddParticulars),` +
        `Inventory(` +
        `$select=InventoryId;$expand=InventoryXtra($select=ContractCode,Swl),` +
        `Problems(` +
        `$select=ProblemId,StatusChanged;$expand=ProblemXtra(` +
        `$select=JobDate,BookedInDateTime),` +
        `Status(` +
        `$select=StatusCode,Description,DeadFlag),` +
        `Type1(` +
        `$select=Type1Id,Description);` +
        `$filter=Status/Deadflag eq 1 and ` +
        `(Type1/Type1Id eq 'INSPEC' or Type1/Type1Id eq 'SERVIC' or Type1/Type1Id eq 'COMM');))`,
      {
        headers: {
          Authorization: "Bearer " + store.state.User.AccessToken
        }
      }
    );

    if (examDetailResponse.status === 200 && examDetailResponse.data) {
      const details = examDetailResponse.data.value[0];
      let proofLoad = "";
      // Default proof load to inventory items SWL from the job detail.
      if (
        details.ProblemXtra.ExamProofLoad &&
        details.ProblemXtra.ExamProofLoad !== ""
      ) {
        proofLoad = details.ProblemXtra.ExamProofLoad;
      }

      // Work out the most recent examination based on the problems linked to the inventory
      // this has to be done client side as OData can't work this out
      const lastExaminationProblem: any = details.Inventory.Problems.sort(
        (a: any, b: any): number => {
          const firstDate = moment(a.StatusChanged);
          const secondDate = moment(b.StatusChanged);

          if (!firstDate.isValid()) {
            return -1;
          } else if (!secondDate.isValid()) {
            return 1;
          } else {
            return secondDate.diff(firstDate);
          }
        }
      )[0];
      // get the last examination date from the most recent problem linked to the inventory item
      let lastExaminationDate = null;
      if (lastExaminationProblem) {
        lastExaminationDate = lastExaminationProblem.ProblemXtra
          .BookedInDateTime
          ? lastExaminationProblem.ProblemXtra.BookedInDateTime
          : lastExaminationProblem.ProblemXtra.JobDate;
      }

      // Configure an examDetail item to be commited to the store based on the OData query
      const examDetail: ExamDetail = {
        JobDate: moment(details.ProblemXtra.JobDate),
        ProofLoad: proofLoad,
        Radius: details.ProblemXtra.ExamRadius,
        LiftSafe: details.ProblemXtra.ExamLiftSafe === 1 ? true : false,
        DefectDetails: details.ProblemXtra.ExamDefectDetails,
        DefectRemedy: details.ProblemXtra.ExamDefectRemedy,
        DefectWork: details.ProblemXtra.ExamDefectWork,
        LeftSite: moment(details.ProblemXtra.ExamLeftSite),
        InaccessibleParts: details.ProblemXtra.EeInaccessible,
        ArrivedOnSite: details.ProblemXtra.ExamArrivedOnSite
          ? moment(details.ProblemXtra.ExamArrivedOnSite)
          : undefined,
        VisitNotes: details.ProblemXtra.ExamVisitNotes,
        ContractCode: details.Inventory.InventoryXtra.ContractCode,
        PreviousExamDate: moment(lastExaminationDate),
        NextExamDate: getNextExamDate(
          details.Inventory.InventoryXtra.ContractCode,
          moment(lastExaminationDate)
        ),
        FactoryThorough: details.ProblemXtra.ExamFactoryThorough,
        FirstAfterInstall: details.ProblemXtra.ExamFirstAfterInstall,
        EqInstallCorrect: details.ProblemXtra.ExamEqInstallCorrect,
        Within6Months: details.ProblemXtra.ExamWithin6Months,
        Within12Months: details.ProblemXtra.ExamWithin12Months,
        InSchedule: details.ProblemXtra.ExamInSchedule,
        AfterExcept: details.ProblemXtra.ExamAfterExcept,
        AddParticulars: details.ProblemXtra.ExamAddParticulars
      };
      await store.commit(MutationNames.SetExamDetail, examDetail);
      store.dispatch(ActionNames.SetProofLoad, proofLoad);
    }
  }
}

async function setPartsDetail(store: ActionContext<State, any>, jobId: number) {
  store.dispatch(ActionNames.SetJobId, jobId);

  if (!store.state.Job.PartsDetail) {
    const partsDetailResponse = await axios.get(
      `${
        store.state.Environment.ODataBaseUrl
      }/Quotes()?$select=QuoteId,ProblemId&$expand=QuoteLines($select=LineId,OperatingCompanyCode` +
        `,ProductItemId,Description,DecimalQuantity,DecimalNetValue;$filter=StatusFlag eq 'A')&$filter=ProblemId eq ${
          store.state.Job.JobId
        } and StatusFlag eq 'A'`,
      {
        headers: {
          Authorization: "Bearer " + store.state.User.AccessToken
        }
      }
    );

    let partsDetail: PartsDetail = {
      Parts: []
    };

    if (partsDetailResponse.status === 200 && partsDetailResponse.data) {
      const details = partsDetailResponse.data.value[0];

      if (details && details.QuoteId) {
        partsDetail = {
          QuoteId: details.QuoteId,
          Parts: details.QuoteLines.map(
            (ql: any) =>
              ({
                OperatingCompanyCode: ql.OperatingCompanyCode,
                ProductItemId: ql.ProductItemId,
                Description: ql.Description,
                QuoteId: details.QuoteId,
                LineId: ql.LineId,
                Quantity: ql.DecimalQuantity,
                DisplayQuantity: Number.parseFloat(ql.DecimalQuantity).toFixed(
                  2
                ),
                DisplayPrice: Number.parseFloat(ql.DecimalNetValue).toFixed(2)
              } as Part)
          )
        };
      }

      store.commit(MutationNames.SetPartsDetail, partsDetail);
    }
  }
}

async function saveChecklistQARow(
  store: ActionContext<State, any>,
  payload: { questionKey: string; answerKey: string }
) {
  if (
    store.state.Job.ChecklistQAs![payload.questionKey] !== payload.answerKey
  ) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ChecklistQAs![payload.questionKey];
    store.commit(MutationNames.SetChecklistQARow, payload);

    const updateObject = {
      Problemid: store.state.Job.JobId,
      Questionkey: payload.questionKey,
      Answerkey: payload.answerKey
    };
    try {
      const response = await postObject(
        store,
        "Checklistjobanswers",
        updateObject
      );

      if (!(response.status === 201 || response.status === 204)) {
        store.commit(MutationNames.SetChecklistQARow, {
          questionKey: payload.questionKey,
          answerKey: rollbackValue
        });
        throw new Error("Failed to update checklist answer");
      }
    } catch (err) {
      store.commit(MutationNames.SetChecklistQARow, {
        questionKey: payload.questionKey,
        answerKey: rollbackValue
      });
      throw new Error("Failed to update checklist answer");
    }

    // Check update was successful and rollback if needed
  }
}
async function setChecklistQA(store: ActionContext<State, any>) {
  await loadQuestions(store);
  const response = await axios.get(
    `${
      store.state.Environment.ODataBaseUrl
    }/ChecklistJobAnswers?$select=Questionkey, Answerkey&$filter=Problemid eq ${
      store.state.Job.JobId
    }`,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken
      }
    }
  );
  if (response.status === 200 && response.data) {
    const questionAndAnswers = response.data.value;
    questionAndAnswers.forEach((q: any) => {
      store.commit(MutationNames.SetChecklistQARow, {
        questionKey: q.Questionkey,
        answerKey: q.Answerkey
      });
    });
  }
}

async function setProofLoad(
  store: ActionContext<State, any>,
  proofLoad: string
) {
  // mutate store so ui updates right away
  // save old value incase a rollback is needed
  const rollbackValue = store.state.Job.ExamDetail!.ProofLoad;
  store.commit(MutationNames.SetProofLoad, proofLoad);

  const updateObject = {
    ExamProofLoad: proofLoad
  };
  const response = await patchObject(
    store,
    "ProblemXtras",
    store.state.Job.JobId,
    updateObject
  );

  // Check update was successful and rollback if needed
  if (response.status < 200 || response.status > 299) {
    store.commit(MutationNames.SetProofLoad, rollbackValue);
    throw new Error("Failed to update Proof Load");
  }
}

async function setRadius(store: ActionContext<State, any>, radius: string) {
  if (store.state.Job.ExamDetail!.Radius !== radius) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.Radius;
    store.commit(MutationNames.SetRadius, radius);
    try {
      const updateObject = {
        ExamRadius: radius
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetRadius, rollbackValue);
        throw new Error("Failed to update Radius");
      }
    } catch (err) {
      store.commit(MutationNames.SetRadius, rollbackValue);
      throw new Error("Failed to update Radius");
    }
  }
}

async function setArrivedOnSite(
  store: ActionContext<State, any>,
  arrivedOnSite: moment.Moment
) {
  if (store.state.Job.ExamDetail!.ArrivedOnSite !== arrivedOnSite) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.ArrivedOnSite;
    store.commit(MutationNames.SetArrivedOnSite, arrivedOnSite);
    try {
      const updateObject = {
        ExamArrivedOnSite: arrivedOnSite
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetArrivedOnSite, rollbackValue);
        throw new Error("Failed to update Arrived on Site");
      }
    } catch (err) {
      store.commit(MutationNames.SetArrivedOnSite, rollbackValue);
      throw new Error("Failed to update Arrived on Site");
    }
  }
}

async function setRoteIncluded(
  store: ActionContext<State, any>,
  newValue: boolean | null
) {
  if (store.state.Job.JobDetail!.ROTEIncluded !== newValue) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.JobDetail!.ROTEIncluded;
    store.commit(MutationNames.SetRoteIncluded, newValue);
    try {
      const updateObject = {
        StandardFlagField5: newValue
      };
      const response = await patchObject(
        store,
        "InventoryXtras",
        store.state.Job.JobDetail!.InventoryId!,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetRoteIncluded, rollbackValue);
        throw new Error("Failed to update Rote included");
      }
    } catch (err) {
      store.commit(MutationNames.SetRoteIncluded, rollbackValue);
      throw new Error("Failed to update Rote included");
    }
  }
}

async function setBookedInDateTime(
  store: ActionContext<State, any>,
  bookedInDateTime: moment.Moment
) {
  if (store.state.Job.JobDetail!.BookedInDateTime !== bookedInDateTime) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.JobDetail!.BookedInDateTime;
    store.commit(MutationNames.SetBookedInDateTime, bookedInDateTime);
    try {
      const updateObject = {
        BookedInDateTime: bookedInDateTime
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetBookedInDateTime, rollbackValue);
        throw new Error("Failed to update Arrived on Site");
      }
    } catch (err) {
      store.commit(MutationNames.SetBookedInDateTime, rollbackValue);
      throw new Error("Failed to update Arrived on Site");
    }
  }
}

async function setNextRoteDue(
  store: ActionContext<State, any>,
  nextRoteDue: moment.Moment
) {
  if (store.state.Job.JobDetail!.NextRoteDue !== nextRoteDue) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.JobDetail!.NextRoteDue;
    store.commit(MutationNames.SetNextRoteDue, nextRoteDue);
    try {
      const updateObject = {
        StandardDateField5: nextRoteDue
      };
      const response = await patchObject(
        store,
        "InventoryXtras",
        store.state.Job.JobDetail!.InventoryId!,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetNextRoteDue, rollbackValue);
        throw new Error("Failed to update Next Rote Due");
      }
    } catch (err) {
      store.commit(MutationNames.SetNextRoteDue, rollbackValue);
      throw new Error("Failed to update Next Rote Due");
    }
  }
}

async function setLiftSafe(
  store: ActionContext<State, any>,
  liftSafe: boolean
) {
  // mutate store so ui updates right away
  // save old value incase a rollback is needed
  if (store.state.Job.ExamDetail!.LiftSafe !== liftSafe) {
    const rollbackValue = store.state.Job.ExamDetail!.LiftSafe;
    store.commit(MutationNames.SetLiftSafe, liftSafe);
    try {
      const updateObject = {
        ExamLiftSafe: liftSafe ? 1 : 0
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetLiftSafe, rollbackValue);
        throw new Error("Failed to update Lift Safety");
      }
    } catch (err) {
      store.commit(MutationNames.SetLiftSafe, rollbackValue);
      throw new Error("Failed to update Lift Safety");
    }
  }
}

async function setDefectDetails(
  store: ActionContext<State, any>,
  defectDetails: string
) {
  if (store.state.Job.ExamDetail!.DefectDetails !== defectDetails) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.DefectDetails;
    store.commit(MutationNames.SetDefectDetails, defectDetails);
    try {
      const updateObject = {
        ExamDefectDetails: defectDetails
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetDefectDetails, rollbackValue);
        throw new Error("Failed to update Defect Details");
      }
    } catch (err) {
      store.commit(MutationNames.SetDefectDetails, rollbackValue);
      throw new Error("Failed to update Defect Details");
    }
  }
}

async function setDefectRemedy(
  store: ActionContext<State, any>,
  defectRemedy: string
) {
  if (store.state.Job.ExamDetail!.DefectRemedy !== defectRemedy) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.DefectRemedy;
    store.commit(MutationNames.SetDefectRemedy, defectRemedy);
    try {
      const updateObject = {
        ExamDefectRemedy: defectRemedy
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetDefectRemedy, rollbackValue);
        throw new Error("Failed to update Defect Remedy");
      }
    } catch (err) {
      store.commit(MutationNames.SetDefectRemedy, rollbackValue);
      throw new Error("Failed to update Defect Remedy");
    }
  }
}

async function setDefectWork(
  store: ActionContext<State, any>,
  defectWork: string
) {
  if (store.state.Job.ExamDetail!.DefectWork !== defectWork) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.DefectWork;
    store.commit(MutationNames.SetDefectWork, defectWork);
    try {
      const updateObject = {
        ExamDefectWork: defectWork
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetDefectWork, rollbackValue);
        throw new Error("Failed to update Defect Work");
      }
    } catch (err) {
      store.commit(MutationNames.SetDefectWork, rollbackValue);
      throw new Error("Failed to update Defect Work");
    }
  }
}

async function setInaccessibleParts(
  store: ActionContext<State, any>,
  inaccessibleParts: string
) {
  if (store.state.Job.ExamDetail!.InaccessibleParts !== inaccessibleParts) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.InaccessibleParts;
    store.commit(MutationNames.SetInaccessibleParts, inaccessibleParts);
    try {
      const updateObject = {
        EeInaccessible: inaccessibleParts
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetInaccessibleParts, rollbackValue);
        throw new Error("Failed to update Inaccessible Parts");
      }
    } catch (err) {
      store.commit(MutationNames.SetInaccessibleParts, rollbackValue);
      throw new Error("Failed to update Inaccessible Parts");
    }
  }
}

async function setVisitNotes(
  store: ActionContext<State, any>,
  visitNotes: string
) {
  if (store.state.Job.ExamDetail!.VisitNotes || "" !== visitNotes) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.ExamDetail!.VisitNotes;
    store.commit(MutationNames.SetVisitNotes, visitNotes);
    try {
      const updateObject = {
        ExamVisitNotes: visitNotes
      };
      const response = await patchObject(
        store,
        "ProblemXtras",
        store.state.Job.JobId,
        updateObject
      );

      // Check update was successful and rollback if needed
      if (response.status !== 204) {
        store.commit(MutationNames.SetVisitNotes, rollbackValue);
        throw new Error("Failed to update Visit Notes");
      }
    } catch (err) {
      store.commit(MutationNames.SetVisitNotes, rollbackValue);
      throw new Error("Failed to update Visit Notes");
    }
  }
}

async function setSafeWorkingLoad(
  store: ActionContext<State, any>,
  swl: string
) {
  if (store.state.Job.JobDetail!.Swl || "" !== swl) {
    // mutate store so ui updates right away
    // save old value incase a rollback is needed
    const rollbackValue = store.state.Job.JobDetail!.Swl;
    store.commit(MutationNames.SetSafeWorkingLoad, swl);
    try {
      const updateObject = {
        Swl: swl
      };

      try {
        await patchObject(
          store,
          "InventoryXtras",
          store.state.Job.JobDetail!.InventoryId!,
          updateObject
        );
      } catch (err) {
        store.commit(MutationNames.SetSafeWorkingLoad, rollbackValue);
        throw new Error("Failed to update SWL");
      }
    } catch (err) {
      store.commit(MutationNames.SetSafeWorkingLoad, rollbackValue);
      throw new Error("Failed to update SWL");
    }
  }
}

async function createQuote(store: ActionContext<State, any>) {
  if (
    store.state.Job.JobDetail &&
    store.state.Job.PartsDetail &&
    !store.state.Job.PartsDetail.QuoteId
  ) {
    const object = {
      OperatingCompanyCode:
        store.state.Job.JobDetail.DivisionOperatingCompanyCode,
      ProblemId: store.state.Job.JobId,
      Description: "Required Parts",
      QuoteDate: moment(),
      EndDate: moment().add(1, "month")
    };

    try {
      const response = await postObject(store, "Quotes", object);
      store.commit(MutationNames.SetQuoteId, response.data.QuoteId);
    } catch (err) {
      throw new Error("Failed to create quote");
    }
  }
}

async function addPartToQuote(
  store: ActionContext<State, any>,
  payload: { product: ProductItem; quantity: number; price?: number }
) {
  if (payload.product && store.state.Job.PartsDetail) {
    // If we don't have a quoteId we need to create a quote
    if (!store.state.Job.PartsDetail.QuoteId) {
      try {
        await store.dispatch(ActionNames.CreateQuote);
      } catch (err) {
        throw new Error("Failed to create quote.");
      }
    }

    const object = {
      QuoteId: store.state.Job.PartsDetail.QuoteId,
      OperatingCompanyCode: payload.product.OperatingCompanyCode,
      ProductItemId: payload.product.ProductItemId,
      Description: payload.product.Description,
      Quantity: payload.quantity
    } as any;

    try {
      const postResponse = await postObject(store, "Quotelines", object);
      if (postResponse.status === 201 && postResponse.data) {
        const line = postResponse.data;

        const part = Object.assign(payload.product, {
          QuoteId: line.QuoteId,
          LineId: line.LineId,
          Quantity: line.Quantity,
          DisplayQuantity: Number.parseFloat(line.DecimalQuantity).toFixed(2),
          DisplayPrice: Number.parseFloat(line.DecimalNetValue).toFixed(2)
        });

        if ("price" in payload && payload.price !== undefined) {
          const updateObject = {
            ForeignCurrencyPrice: payload.price
          };

          const patchResponse = await patchObject(
            store,
            "Quotelines",
            line.LineId,
            updateObject
          );
          if (patchResponse.status === 204) {
            const DecimalNetValue = payload.price * payload.quantity;
            part.DisplayPrice = (
              DecimalNetValue / Math.pow(10, line.SellDecimals)
            ).toFixed(line.SellDecimals);
          }
        }

        store.commit(MutationNames.AddPartToQuote, part);
      }
    } catch (err) {
      throw new Error("Failed to add part");
    }
  } else {
    throw new Error("Failed to add part");
  }
}

async function removePartFromQuote(
  store: ActionContext<State, any>,
  lineid: number
) {
  // Only delete if parts has an item of that line id
  if (store.state.Job.PartsDetail) {
    const partIndex = store.state.Job.PartsDetail.Parts.findIndex(p => {
      return p.LineId === lineid;
    });

    if (partIndex !== undefined) {
      const rollbackValue = Object.assign(
        {},
        store.state.Job.PartsDetail.Parts[partIndex]
      );
      store.commit(MutationNames.RemovePartFromQuote, partIndex);

      try {
        await deleteObject(store, "QuoteLines", lineid);
      } catch (err) {
        store.commit(MutationNames.AddPartToQuoteAtIndex, {
          partIndex,
          part: rollbackValue
        });
        throw new Error("Failed to delete quote line");
      }
    }
  }
}

async function closeJob(
  store: ActionContext<State, any>,
  payload: {
    signature: string;
    signatory: string;
    email: string;
    statusDetailId?: string;
  }
) {
  try {
    // update problem xtra fields
    const problemXtraPatch = {
      ExamLeftSite: moment(),
      JobDate: moment(),
      ExamSignatory: payload.signatory,
      Checklistcomplete: 1
    };
    await patchObject(
      store,
      "ProblemXtras",
      store.state.Job.JobId,
      problemXtraPatch
    );
    try {
      // post a new document empty to Documents()
      const object = {
        ProblemId: store.state.Job.JobId,
        DocumentTypeCode: "EXTERN",
        Description: "Signature"
      };
      const response = await postObject(store, "Documents", object);
      if (!(response.status === 201 || response.status === 204)) {
        throw new Error("Failed to upload Document");
      }
      // get id of doc
      const documentId = response.data.DocumentId;

      // split 64
      const base64Sig = payload.signature.split(",")[1];

      // use id to post base64
      const result = await postSignature(
        store,
        "Documents",
        documentId,
        base64Sig
      );
    } catch (e) {
      throw new Error("Failed to complete job");
    }

    // set the status of job to COMPLT and detail to SUCESS
    const problemPatch = {
      StatusId: "COMPLT",
      StatusDetailId: payload.statusDetailId || "SUCESS"
    };
    await patchObject(store, "Problems", store.state.Job.JobId, problemPatch);

    // find out if we are meant to have a service charge
    const serviceCharge = serviceChargeCalulator.calculateCharge(
      store.state.Job.JobDetail!.DivisionOperatingCompanyCode,
      store.state.Job.JobDetail!.JobType || "",
      store.state.Job.JobDetail!.InventoryTypeId || "",
      store.state.Job.JobDetail!.InventoryProduct || ""
    );

    // if we have a service charge product check if it exists, if not add it
    if (
      serviceCharge.ProductItemId !== "" &&
      !checkForServiceProduct(store, serviceCharge.ProductItemId)
    ) {
      await store.dispatch(ActionNames.AddPartToQuote, {
        product: serviceCharge,
        quantity: 1
      });
    }

    // if inspection or service update inventory using contract code
    // get next job date and next job type and save on inventoryxtra
    if (
      store.state.Job.JobDetail!.JobType === JobTypes.INSPECTION ||
      store.state.Job.JobDetail!.JobType === JobTypes.SERVICE
    ) {
      const inventoryXtraPatch = {
        StandardDateField5:
          store.state.Job.JobDetail!.NextRoteDue ||
          moment()
            .startOf("day")
            .add(1, "year")
      };
      await patchObject(
        store,
        "InventoryXtras",
        store.state.Job.JobDetail!.InventoryId!,
        inventoryXtraPatch
      );
    }
  } catch (err) {
    throw new Error("Failed to close job. ");
  }
}
async function postAttachExistingDocuments(
  store: ActionContext<State, any>,
  checklistObject: object,
  examDetailsObject: object
) {
  const data = {
    AttachmentId: "",
    DocumentNos: [checklistObject, examDetailsObject],
    Pdf: true
  };

  const url = store.state.Environment.ODataBaseUrl;
  const result = await axios.post(
    `${url}/DocumentAttachments/AttachExistingDocument`,
    data,
    {
      headers: {
        Authorization: "Bearer " + store.state.User.AccessToken,
        "content-type": "application/JSON"
      }
    }
  );
  return result;
}

async function postDocuments(
  store: ActionContext<State, any>,
  DocumentTypeCodeArg: string,
  DescriptionArg: string
) {
  const url = store.state.Environment.ODataBaseUrl;

  const updateObject = {
    ProblemId: store.state.Job.JobId,
    DocumentTypeCode: DocumentTypeCodeArg,
    Description: DescriptionArg,
    DocumentDate: new Date().toISOString()
  };

  const result = await axios.post(`${url}/${"Documents"}`, updateObject, {
    headers: {
      Authorization: "Bearer " + store.state.User.AccessToken,
      "content-type": "application/JSON"
    }
  });
  const DocumentInfo = {
    DocumentNo: result.data.DocumentId,
    Name: result.data.FileName.replace(/\.[^/.]+$/, "") + ".pdf"
  };

  return DocumentInfo;
}
const Actions: ActionTree<State, any> = {
  setJobId,
  setJobDetail,
  setExamDetail,
  setPartsDetail,
  setChecklistQA,
  saveChecklistQARow,
  setProofLoad,
  setRadius,
  setArrivedOnSite,
  setBookedInDateTime,
  setLiftSafe,
  setDefectDetails,
  setDefectRemedy,
  setDefectWork,
  setInaccessibleParts,
  setVisitNotes,
  setSafeWorkingLoad,
  createQuote,
  addPartToQuote,
  removePartFromQuote,
  closeJob,
  setRoteIncluded,
  setNextRoteDue,
  setRoteToggleQuestion,
  setAdditionalParticulars
};
export default Actions;
